// ===========================================
// DOMAIN ROUTE GUARD
// ===========================================

import { Navigate, useLocation } from 'react-router-dom';
import { isAdminDomain } from '@/utils/domain';
import type { ReactNode } from 'react';

interface DomainGuardProps {
    children: ReactNode;
    requireAdmin?: boolean;
}

/**
 * Guard that enforces domain-based route access:
 * - Admin routes (/admin/*) only accessible on admin/dashboard domain
 * - Public routes only accessible on public domain
 */
export function DomainGuard({ children, requireAdmin = false }: DomainGuardProps) {
    const location = useLocation();
    const isAdmin = isAdminDomain();

    // Development environment handling
    if (import.meta.env.DEV) {
        // If requireAdmin but no admin detected (no /admin prefix or VITE_APP_MODE)
        if (requireAdmin && !isAdmin) {
            return <Navigate to="/admin/login" replace />;
        }

        // If public route but in admin mode
        if (!requireAdmin && isAdmin) {
            return <Navigate to="/" replace />;
        }

        return <>{children}</>;
    }

    // Production environment handling
    // If this route requires admin domain but we're on public domain
    if (requireAdmin && !isAdmin) {
        // Redirect to admin domain with same path
        const adminUrl = `https://dashboard.tunistravels.com${location.pathname}${location.search}`;
        window.location.href = adminUrl;
        return null;
    }

    // If this is a public route but we're on admin domain
    if (!requireAdmin && isAdmin) {
        // Redirect to public domain with same path
        const publicUrl = `https://tunistravels.com${location.pathname}${location.search}`;
        window.location.href = publicUrl;
        return null;
    }

    return <>{children}</>;
}
